// rimPillStack.js — THE rim-pill stack layout, shared by both wheel engines
// (D3Wheels/createPie.js = Edit Wheel / Builder, renderer-v2/engine.js = View
// Wheel). stgfix-10120830-rimpills-kelly (T4.18 / T22.129 / T22.130, Kelly +
// Gordon 2026-10-12, wheel 507342, 33 segments): the two engines each carried
// their own copy of the Pill Rule A pass and the copies had drifted —
//   - Edit Wheel ran the residual X pass AFTER restoring the pill transitions,
//     so every relayout animated pills through an overlap for 220ms;
//   - View Wheel never bounded the stack by the room it really has and never
//     ran the residual X pass at all.
// Both now call layoutRimPillStack, so the pills land in the SAME place on
// both pages. Engine-specific transform writing stays in each engine's
// callbacks (moveY / moveRadial / moveX); the measuring, the stack, the ring
// re-check and the residual pass live only here.

import {
  resolveTightPillStack,
  resolveResidualPillOverlaps,
  runRingSafetyCorrectionLoop,
} from "./labelLayout";
import { findClipBoundaryRect } from "./labelRadialX";

// The room the pills really have, in page px. Top = the clipping ancestor (the
// canvas pane); bottom = the Builder's fit area when the wheel sits in one
// (review-1008-Frontend-6764), else the wheel's own <svg> box; both inside the
// window. minX/maxX bound the residual X pass the same way. `vertical:
// false` (View Wheel) leaves the stack vertically unbounded, as it always was
// there: the page scrolls and the pills paint past the <svg> (overflow
// visible).
export function rimPillStackRoom(svgEl, parentEl, win, { vertical = true } = {}) {
  let root = svgEl || null;
  while (root && root.ownerSVGElement) root = root.ownerSVGElement;
  const clip = parentEl ? findClipBoundaryRect(parentEl) : null;
  const hasClip = !!(clip && clip.height > 0);
  const roomEl =
    (root && typeof root.closest === "function"
      ? root.closest('[data-testid="builder-wheel-fit-area"]')
      : null) || root;
  const roomRect = roomEl ? roomEl.getBoundingClientRect() : null;
  // stgfix-10130814-T22131: controls painted OVER the canvas (the Builder's
  // Undo/Redo cluster, marked data-rim-pill-obstacle) — the room fallback in
  // layoutRimPillStack never shifts a pill under one.
  const doc = (root && root.ownerDocument) || (parentEl && parentEl.ownerDocument) || null;
  const obstacles = doc
    ? Array.from(doc.querySelectorAll("[data-rim-pill-obstacle]"))
        .map((el) => el.getBoundingClientRect())
        .filter((r) => r && r.width > 0 && r.height > 0)
        .map((r) => ({ left: r.left, right: r.right, top: r.top, bottom: r.bottom }))
    : [];
  const winH = win && win.innerHeight > 0 ? win.innerHeight : Infinity;
  const winW = win && win.innerWidth > 0 ? win.innerWidth : Infinity;
  return {
    minTop: !vertical ? -Infinity : Math.max(0, hasClip ? clip.top : 0),
    maxBottom: !vertical ? Infinity : Math.min(
      winH,
      hasClip ? clip.bottom : Infinity,
      roomRect && roomRect.height > 0 ? roomRect.bottom : Infinity
    ),
    minX: clip ? Math.max(0, clip.left) : 0,
    maxX: clip ? Math.min(winW, clip.right) : winW,
    obstacles,
  };
}

function hasPillOverlap(boxes) {
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const a = boxes[i];
      const b = boxes[j];
      const ox = Math.min(a.right, b.right) - Math.max(a.left, b.left);
      const oy = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
      if (ox > 0.5 && oy > 0.5) return true;
    }
  }
  return false;
}

// pills: [{ id, midAngle, measureEl, transitionEl, moveY(dyPx), moveRadial(px), moveX(dxPx) }]
// in clockwise order. ring: { center, radius } of the wedges in page px (null
// skips the ring re-check and the residual pass, as before).
export function layoutRimPillStack(pills, { room, ring, requiredGapPx }) {
  const list = (pills || []).filter((p) => p && p.measureEl);
  if (!list.length) return;
  // Every pill carries a transform transition (hover grow). Suspend them for
  // the WHOLE pass — measure, stack, ring re-check AND residual shift — so no
  // move animates and every read is the real current box.
  const prev = list.map((p) => (p.transitionEl && p.transitionEl.style ? p.transitionEl.style.transition : undefined));
  list.forEach((p) => {
    if (p.transitionEl && p.transitionEl.style) p.transitionEl.style.transition = "none";
  });
  try {
    const rects = [];
    for (const p of list) {
      const r = p.measureEl.getBoundingClientRect();
      if (!(r.width > 0 && r.height > 0)) continue;
      rects.push({ p, id: p.id, midAngle: p.midAngle, left: r.left, right: r.right, top: r.top, bottom: r.bottom });
    }
    if (!rects.length) return;

    const placed = new Map(
      resolveTightPillStack(rects, {
        minTop: room.minTop,
        maxBottom: room.maxBottom,
        // stgfix-10120932-rimpills-symmetry: two pills competing for 12/6
        // o'clock sit side by side, mirrored about the wheel's axis.
        axisX: ring && ring.center ? ring.center.x : undefined,
      }).map((x) => [x.p, x])
    );
    for (const { p } of rects) {
      const s = placed.get(p);
      if (!s || (!s.dy && !s.dx)) continue;
      if (s.dx) p.moveX(s.dx);
      if (s.dy) p.moveY(s.dy);
      // Ring safety ("titles never overlap the wheel") outranks Pill Rule A.
      if (ring) {
        runRingSafetyCorrectionLoop({
          measureEl: p.measureEl,
          transitionEl: p.transitionEl,
          wedgeCenter: ring.center,
          wedgeRadius: ring.radius,
          requiredGapPx,
          onCorrect: (c) => p.moveRadial(c),
        });
      }
    }

    // audit-10101940-box-editwheel-1: ring safety can push a stacked pill back
    // onto its neighbour — clear what is left with a minimal X shift away from
    // the wheel's vertical axis (resolveResidualPillOverlaps header).
    if (ring) {
      const measureResidual = () => {
        const residual = [];
        for (const { p } of rects) {
          const r = p.measureEl.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) residual.push({ id: p, left: r.left, right: r.right, top: r.top, bottom: r.bottom });
        }
        return residual;
      };
      const shifts = resolveResidualPillOverlaps(measureResidual(), {
        axisX: ring.center.x,
        minX: room.minX,
        maxX: room.maxX,
      });
      for (const { id: p, dx } of shifts) p.moveX(dx);

      // stgfix-10130814-T22131 (Kelly T22.131, MEASURED web-stg main.608439c0,
      // /builder/507106 @1440x900, 34 segments): the two two-line pills at
      // 12 o'clock (5.3deg / 15.9deg) stay fully stacked, 133x27px. The pole
      // stack wanted to lift the outer one ~40px but the room's top (the
      // canvas clip, 72px) scaled that lift away, and the capped X pass above
      // refuses a shift wider than 0.75 of the pill (133 of ~150px). Only when
      // an overlap is LEFT (so a clean layout is never touched): shift by the
      // full overlap (inside minX/maxX, never under a canvas control), then
      // re-run the room-bounded stack on the new boxes for whatever the shift
      // could not clear — side by side now leaves the stack the room it needs.
      if (hasPillOverlap(measureResidual())) {
        const fallback = resolveResidualPillOverlaps(measureResidual(), {
          axisX: ring.center.x,
          minX: room.minX,
          maxX: room.maxX,
          maxShiftFraction: Infinity,
          obstacles: room.obstacles,
        });
        for (const { id: p, dx } of fallback) p.moveX(dx);
        const now = [];
        for (const { p } of rects) {
          const r = p.measureEl.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) now.push({ p, id: p.id, midAngle: p.midAngle, left: r.left, right: r.right, top: r.top, bottom: r.bottom });
        }
        if (hasPillOverlap(now)) {
          for (const s of resolveTightPillStack(now, {
            minTop: room.minTop,
            maxBottom: room.maxBottom,
            axisX: ring.center.x,
          })) {
            if (!s.dy && !s.dx) continue;
            if (s.dx) s.p.moveX(s.dx);
            if (s.dy) s.p.moveY(s.dy);
            runRingSafetyCorrectionLoop({
              measureEl: s.p.measureEl,
              transitionEl: s.p.transitionEl,
              wedgeCenter: ring.center,
              wedgeRadius: ring.radius,
              requiredGapPx,
              onCorrect: (c) => s.p.moveRadial(c),
            });
          }
        }
      }
    }
  } finally {
    // Commit the final boxes with transitions still off, then restore.
    list.forEach((p) => p.measureEl.getBoundingClientRect());
    list.forEach((p, i) => {
      if (p.transitionEl && p.transitionEl.style) p.transitionEl.style.transition = prev[i];
    });
  }
}
